import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_API_BASE_PATH, LUPP_QUERY_KEYS } from '../constants.lupp';
import { isTestDriveHostAssignmentEnabled } from '../../../../config/featureFlags';
import {
  ProspectTestDrive,
  ProspectTestDrivesResponse,
  CreateProspectTestDriveRequest,
  UpdateProspectTestDriveRequest,
  TestDriveStatus,
} from './dto.test-drive-schedule';

const testDrivesUrl = (prospectId: string) =>
  `${LUPP_API_BASE_PATH}/prospects/${prospectId}/test-drives`;

/*
|--------------------------------------------------------------------------
| REQUEST SHAPING
|--------------------------------------------------------------------------
| x-sales's ProspectTestDriveRequest only declares these 11 fields; ids,
| ownership (businessPartnerUserId/OrganizationId), lifecycle flags
| (isActive/isDeleted) and cancellationNotes are server-controlled and are
| NOT part of its contract. Sending them is harmless today (Jackson ignores
| unknown properties by default) but relying on that would be fragile, so
| only the BE-accepted fields are ever put on the wire.
*/
// Note: ProspectTestDriveRequest also accepts a `description` field (free-text vehicle
// description); the FE has no UI producing that value today, so it's intentionally omitted here.
const toApiRequestPayload = (
  data: CreateProspectTestDriveRequest | UpdateProspectTestDriveRequest
) => ({
  testDriveStatus: data.testDriveStatus,
  testDriveType: data.testDriveType,
  scheduledStartDatetime: data.scheduledStartDatetime,
  scheduledEndDatetime: data.scheduledEndDatetime,
  appointmentLocationName: data.appointmentLocationName,
  model: data.model,
  trim: data.trim,
  timezone: data.timezone,
  eventConfigId: data.eventConfigId,
  vin: data.vin,
  cancellationReason: data.cancellationReason,
  ...(isTestDriveHostAssignmentEnabled() && { hostUserIdentifier: data.hostUserIdentifier }),
});

/*
|--------------------------------------------------------------------------
| STATUS NORMALIZATION
|--------------------------------------------------------------------------
| ProspectTestDriveRequest validates status case-insensitively. Normalize
| whatever casing/vocabulary comes back over the wire onto the FE's
| lowercase union. "Requested" (e.g. a booking made on the website) is kept
| as its own status so the UI shows what the API returns.
*/
const STATUS_NORMALIZATION: Record<string, TestDriveStatus> = {
  requested: 'requested',
  scheduled: 'scheduled',
  confirmed: 'confirmed',
  completed: 'completed',
  'no show': 'no show',
  cancelled: 'cancelled',
};

const normalizeTestDriveStatus = (status?: string): TestDriveStatus | undefined => {
  if (!status) return undefined;
  const key = status
    .toLowerCase()
    .replace(/[_\s]+/g, ' ')
    .trim();
  return STATUS_NORMALIZATION[key];
};

const normalizeTestDrive = (testDrive: ProspectTestDrive): ProspectTestDrive => ({
  ...testDrive,
  testDriveStatus: normalizeTestDriveStatus(testDrive.testDriveStatus) ?? testDrive.testDriveStatus,
});

/*
|--------------------------------------------------------------------------
| GET PROSPECT TEST DRIVES
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch every test drive booked for a prospect.
 * Calls x-sales's `GET /v1/prospects/{prospectId}/test-drives` (TestDriveManagementController).
 *
 * A booking's status can change on the backend without any FE-initiated mutation (e.g. TDM marks
 * a test drive `completed` once the check-in/check-out flow finishes). The app disables
 * `refetchOnWindowFocus` globally, so without overriding it here a tab left open on this page would
 * keep showing the stale status it had on mount indefinitely. Refetching on focus ensures the badge
 * and details view reflect the latest status once the user returns to the tab.
 */
export const useGetProspectTestDrives = (prospectId: string, enabled = true) => {
  return useQuery<ProspectTestDrivesResponse>({
    queryKey: [LUPP_QUERY_KEYS.PROSPECT_TEST_DRIVES, prospectId],
    queryFn: async () => {
      const response = await LuppApi.get<ProspectTestDrivesResponse>(testDrivesUrl(prospectId));
      return response.map(normalizeTestDrive);
    },
    enabled: enabled && Boolean(prospectId),
    refetchOnWindowFocus: true,
  });
};

/*
|--------------------------------------------------------------------------
| CREATE PROSPECT TEST DRIVE
|--------------------------------------------------------------------------
*/

/**
 * Hook to book a new, independent test drive for a prospect.
 * Calls x-sales's `POST /v1/prospects/{prospectId}/test-drives` (TestDriveManagementController).
 */
export const useCreateProspectTestDrive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      prospectId,
      data,
    }: {
      prospectId: string;
      data: CreateProspectTestDriveRequest;
    }) => {
      const response = await LuppApi.post<
        ReturnType<typeof toApiRequestPayload>,
        ProspectTestDrive
      >(testDrivesUrl(prospectId), toApiRequestPayload(data));
      return normalizeTestDrive(response);
    },
    onSuccess: (_created, variables) => {
      queryClient.invalidateQueries({
        queryKey: [LUPP_QUERY_KEYS.PROSPECT_TEST_DRIVES, variables.prospectId],
      });
    },
  });
};

/*
|--------------------------------------------------------------------------
| UPDATE PROSPECT TEST DRIVE
|--------------------------------------------------------------------------
*/

/**
 * Hook to modify, complete, or cancel a single existing test drive without affecting any
 * others booked for the same prospect.
 * Calls x-sales's `PATCH /v1/prospects/{prospectId}/test-drives/{testDriveId}`
 * (TestDriveManagementController).
 */
export const useUpdateProspectTestDrive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      prospectId,
      testDriveId,
      data,
    }: {
      prospectId: string;
      testDriveId: string;
      data: UpdateProspectTestDriveRequest;
    }) => {
      const response = await LuppApi.patch<
        ReturnType<typeof toApiRequestPayload>,
        ProspectTestDrive
      >(`${testDrivesUrl(prospectId)}/${testDriveId}`, toApiRequestPayload(data));
      return normalizeTestDrive(response);
    },
    onSuccess: (_updated, variables) => {
      queryClient.invalidateQueries({
        queryKey: [LUPP_QUERY_KEYS.PROSPECT_TEST_DRIVES, variables.prospectId],
      });
    },
  });
};
